"use client";
import { CalendarIcon } from "lucide-react";
import { format } from "date-fns";
import { useFormContext } from "react-hook-form";

import { Input } from "@/components/ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Card } from "@/components/ui/card";
import { Calendar } from "@/components/ui/calendar";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import { cn, formatDate } from "@/lib/utils";
import {
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import { Button } from "@/components/ui/button";
import { bullTypes, nigerianBreeds, sex } from "@/utils/items.ts";

export function BasicInfoForm() {
  const { getValues, control, setValue } = useFormContext();

  console.log("birthdate", getValues("birthdate"));
  // const [date, setDate] = useState<Date | undefined>(
  //   watch("birthDate") ? new Date(watch("birthDate")) : undefined,
  // );

  // console.log("date", getValues("birthdate"));
  const handleDateSelect = (date: Date | undefined) => {
    // setDate(date);
    if (date) {
      setValue("birthDate", formatDate(date));

      // Calculate age in months based on birth date
      const today = new Date();
      const birthDate = new Date(date);

      let ageInMonths =
        (today.getFullYear() - birthDate.getFullYear()) * 12 +
        (today.getMonth() - birthDate.getMonth());

      // Adjust for day of month if needed
      if (today.getDate() < birthDate.getDate()) {
        ageInMonths--;
      }

      setValue("age", ageInMonths);
    } else {
      // Clear the age if date is cleared
      setValue("age", undefined);
    }
  };

  return (
    <div className="space-y-6">
      <h2 className="text-2xl font-bold text-center mb-6 text-[#015354ff]">
        Basic Information
      </h2>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <FormField
          control={control}
          name="dgr"
          render={({ field }) => (
            <FormItem>
              <FormLabel className={"text-[#008c8fff]"}>
                DGR (Digital Genetic Number)
              </FormLabel>
              <FormControl>
                <Input placeholder="Enter DGR" {...field} />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />

        {/* <FormField */}
        {/*   control={control} */}
        {/*   name="birthDate" */}
        {/*   render={() => ( */}
        {/*     <FormItem className="flex flex-col"> */}
        {/*       <FormLabel className={"text-[#008c8fff]"}>Birth Date</FormLabel> */}
        {/*       <Popover> */}
        {/*         <PopoverTrigger asChild> */}
        {/*           <FormControl> */}
        {/*             <Button */}
        {/*               className={cn( */}
        {/*                 "w-full pl-3 text-left font-normal", */}
        {/*                 !date && "text-muted-foreground", */}
        {/*               )} */}
        {/*               variant="outline" */}
        {/*             > */}
        {/*               <CalendarIcon className="mr-2 h-4 w-4" /> */}
        {/*               {date ? format(date, "PPP") : <span>Select date</span>} */}
        {/*             </Button> */}
        {/*           </FormControl> */}
        {/*         </PopoverTrigger> */}
        {/*         <PopoverContent align="start" className="w-auto p-0"> */}
        {/*           <Calendar */}
        {/*             initialFocus */}
        {/*             disabled={(date) => date > new Date()} */}
        {/*             mode="single" */}
        {/*             selected={date} */}
        {/*             onSelect={handleDateSelect} */}
        {/*           /> */}
        {/*         </PopoverContent> */}
        {/*       </Popover> */}
        {/*       <FormMessage /> */}
        {/*     </FormItem> */}
        {/*   )} */}
        {/* /> */}

        <div
          className={"w-full flex flex-col lg:flex-row gap-10 lg:items-center"}
        >
          <FormField
            control={control}
            name="birthdate"
            render={({ field }) => (
              <FormItem className="flex flex-col">
                <FormLabel className={"text-[#008c8fff]"}>
                  Date of birth
                </FormLabel>
                <Popover>
                  <PopoverTrigger asChild>
                    <FormControl>
                      <Button
                        variant={"outline"}
                        className={cn(
                          "lg:w-[315px] pl-3 text-left font-normal",
                          !field.value && "text-muted-foreground",
                        )}
                      >
                        {field.value ? (
                          format(field.value, "PPP")
                        ) : (
                          <span>Pick a date</span>
                        )}
                        <CalendarIcon className="ml-auto h-4 w-4 opacity-50" />
                      </Button>
                    </FormControl>
                  </PopoverTrigger>
                  <PopoverContent className="w-auto p-0" align="start">
                    <Calendar
                      mode="single"
                      selected={field.value}
                      onSelect={(date) =>
                        field.onChange(handleDateSelect(date))
                      } // onSelect={ field.onChange }
                      disabled={(date: any) =>
                        date > new Date() || date < new Date("1900-01-01")
                      }
                      captionLayout="dropdown"
                    />
                  </PopoverContent>
                </Popover>
                <FormMessage />
              </FormItem>
            )}
          />

          <FormField
            control={control}
            name="age"
            render={({ field }) => (
              <FormItem>
                <FormLabel className={"text-[#008c8fff]"}>
                  Age (months)
                  {/* {watch("birthDate") && ( */}
                  {/*   <p className="text-xs text-muted-foreground"> */}
                  {/*     Calculated from birth date */}
                  {/*   </p> */}
                  {/* )} */}
                </FormLabel>
                <FormControl>
                  <Input
                    placeholder="Age in months"
                    className={"lg:w-[245px] relative lg:bottom-1"}
                    type="number"
                    {...field}
                    readOnly={!!getValues("birthDate")}
                    onChange={(e) =>
                      field.onChange(
                        e.target.value ? Number(e.target.value) : undefined,
                      )
                    }
                  />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
        </div>

        <FormField
          control={control}
          name="sex"
          render={({ field }) => (
            <FormItem>
              <FormLabel className={"text-[#008c8fff]"}>Sex</FormLabel>
              <Select defaultValue={field.value} onValueChange={field.onChange}>
                <FormControl>
                  <SelectTrigger>
                    <SelectValue placeholder="Select sex" />
                  </SelectTrigger>
                </FormControl>
                <SelectContent
                  className="max-h-[300px] overflow-y-auto z-50 shadow-lg bg-white "
                  position="popper"
                >
                  {sex.map((option) => (
                    <SelectItem
                      key={option.sex}
                      className={"overflow-y-auto z-50 background-white"}
                      value={option.sex}
                    >
                      {option.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <FormMessage />
            </FormItem>
          )}
        />

        <FormField
          control={control}
          name="bullType"
          render={({ field }) => (
            <FormItem>
              <FormLabel className={"text-[#008c8fff]"}>Bull Type</FormLabel>
              <Select defaultValue={field.value} onValueChange={field.onChange}>
                <FormControl>
                  <SelectTrigger>
                    <SelectValue placeholder="Select Bull Type" />
                  </SelectTrigger>
                </FormControl>
                <SelectContent
                  className="max-h-[300px] overflow-y-auto z-50 shadow-lg bg-white "
                  position="popper"
                >
                  {bullTypes.map((option) => (
                    <SelectItem
                      key={option.value}
                      className={"overflow-y-auto z-50 background-white"}
                      value={option.value}
                    >
                      {option.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <FormMessage />
            </FormItem>
          )}
        />

        <FormField
          control={control}
          name="name"
          render={({ field }) => (
            <FormItem>
              <FormLabel className={"text-[#008c8fff]"}>Name</FormLabel>
              <FormControl>
                <Input placeholder="Enter name" {...field} />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />

        <FormField
          control={control}
          name="adg"
          render={({ field }) => (
            <FormItem>
              <FormLabel className={"text-[#008c8fff]"}>
                Average Daily Gain (ADG)
              </FormLabel>
              <FormControl>
                <Input
                  placeholder="Enter ADG"
                  step="0.01"
                  type="number"
                  {...field}
                  onChange={(e) =>
                    field.onChange(
                      e.target.value ? Number(e.target.value) : undefined,
                    )
                  }
                />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />

        <FormField
          control={control}
          name="breed"
          render={({ field }) => (
            <FormItem>
              <FormLabel className={"text-[#008c8fff]"}>Breed</FormLabel>
              <Select onValueChange={field.onChange} defaultValue={field.value}>
                <FormControl>
                  <SelectTrigger>
                    <SelectValue placeholder="Select  breed" />
                  </SelectTrigger>
                </FormControl>
                <SelectContent
                  position="popper"
                  className="max-h-[300px] overflow-y-auto z-50 shadow-lg bg-white "
                >
                  {nigerianBreeds.map((option) => (
                    <SelectItem
                      className={"overflow-y-auto z-50 background-white"}
                      key={option.breed}
                      value={option.breed}
                    >
                      {option.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <FormMessage />
            </FormItem>
          )}
        />

        <FormField
          control={control}
          name="animalType"
          render={({ field }) => (
            <FormItem>
              <FormLabel className={"text-[#008c8fff]"}>Animal Type</FormLabel>
              <Select defaultValue={field.value} onValueChange={field.onChange}>
                <FormControl>
                  <SelectTrigger>
                    <SelectValue placeholder="Select Animal Type" />
                  </SelectTrigger>
                </FormControl>
                <SelectContent
                  className="max-h-[300px] overflow-y-auto z-50 shadow-lg bg-white "
                  position="popper"
                >
                  <SelectItem
                    key={"Live Animal"}
                    className={"overflow-y-auto z-50 background-white"}
                    value={"Live Animal"}
                  >
                    Live Animal
                  </SelectItem>
                  <SelectItem
                    key={"Embryo"}
                    className={"overflow-y-auto z-50 background-white"}
                    value={"Embryo"}
                  >
                    Embryo
                  </SelectItem>
                  <SelectItem
                    key={"Semen"}
                    className={"overflow-y-auto z-50 background-white"}
                    value={"Semen"}
                  >
                    Semen
                  </SelectItem>
                </SelectContent>
              </Select>
              <FormMessage />
            </FormItem>
          )}
        />
      </div>

      <Card className="p-4 bg-slate-50 dark:bg-slate-800 border-slate-200 dark:border-slate-700">
        <p className="text-sm text-slate-600 dark:text-slate-400">
          This section captures the basic identification information for the
          animal. The DGR is a unique identifier, and accurate birth date
          information helps track the animal&#39;s age and development.
        </p>
      </Card>
    </div>
  );
}
